import { useEffect, useRef, type ReactNode } from "react";
import * as RadixPopover from "@radix-ui/react-popover";
import { clsx } from "clsx";

export type PopoverProps = {
  trigger?: ReactNode;
  anchor?: ReactNode;
  className?: string;
  children: ReactNode;
  ref?: React.RefObject<HTMLDivElement | null>;
  open?: boolean;
  modal?: boolean;
  onKeyDown?: (arg: React.KeyboardEvent<HTMLInputElement>) => void;
  onOpenChange?: (arg: boolean) => void;
  content?: {
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
    align?: "start" | "center" | "end";
    alignOffset?: number;
    arrowPadding?: number;
  };
  withoutWrapper?: boolean;
  withArrow?: boolean;
  popoverWrapperClasses?: string;
  withMatchTriggerWidth?: boolean;
}

// change styling of data-radix-popper-content-wrapper
const useApplyParentStyle = (open: boolean, popoverWrapperClasses?: string) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && popoverRef.current) {
      const parent = popoverRef.current.parentElement;
      if (parent?.hasAttribute("data-radix-popper-content-wrapper")) {
        if (popoverWrapperClasses)
          popoverWrapperClasses.split(" ").forEach(c => {
            if (c) {
              parent.classList.add(c);
            }
          });
      }
    }
  }, [open, popoverWrapperClasses]);

  return popoverRef;
};

export const Popover = ({
  trigger,
  anchor,
  children,
  open,
  onKeyDown,
  onOpenChange,
  content,
  className,
  modal = false,
  withArrow = false,
  withoutWrapper,
  popoverWrapperClasses,
  withMatchTriggerWidth = false,
}: PopoverProps) => {
  const popoverRef = useApplyParentStyle(open ?? false, popoverWrapperClasses);

  return (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      {trigger ? <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger> : null}
      {anchor ? <RadixPopover.Anchor asChild>{anchor}</RadixPopover.Anchor> : null}

      <RadixPopover.Content
        ref={popoverRef}
        side={content?.side}
        onKeyDown={onKeyDown}
        className={clsx(
          "no-scrollbar z-30 will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade",
          {
            "min-w-max rounded-2xl bg-white p-5 shadow-lg": !withoutWrapper,
            "w-(--radix-popover-trigger-width) overflow-y-visible": withMatchTriggerWidth,
          },
          className,
        )}
        {...content}>
        {children}

        {withArrow ? <RadixPopover.Arrow className="h-2 w-4 fill-white" /> : null}
      </RadixPopover.Content>
    </RadixPopover.Root>
  );
};
